import { Typography } from "@mui/material";
import { Box, Stack } from "@mui/system";
import HighLevelKpis from "./HighLeveKpis/HighLevelKpis";
import Acquisition from "./Acquisition/Acquisition";
import EmojiObjectsOutlinedIcon from "@mui/icons-material/EmojiObjectsOutlined";
import RevenueStreams from "./RevenueStreams/RevenueStreams";
import {
  typography,
  colors,
} from "../../../../../../../../assets/styles/tokens";
import EfficiencyMetrics from "./EfficiencyMetrics/EfficiencyMetrics";
import PlayerValue from "./PlayerValue/Playervalue";
import KpiRRfilter from "./KpiRRFilter/KpiRRfilter";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { selectUserToken } from "../../../../../../../../features/userSlice";
import { buiDashboardEndPointLogicMobile } from "../../../../../../../../logic/AssetManagement/overview/BusinessLeaderDashboardMobile/buiDashboardEndPointLogicMobile";
import { targetVsActualQueryId } from "../../../helpers/QIDs";
import KpiSkeleton from "../../../helpers/KpiSkeleton";
import { setKpiFetchedKey } from "../../../../../../../../features/businessLeaderDashboardSlice";
import InsightPopup from "../../InsightPopup/InsightPopup";
import { getValueForDashBoard } from "../../../../../BusinessLeaderDashboard/helpers/businessleaderDashboardHelpers";

const KpiandBr = () => {
  const [dateRange, setDateRange] = useState<{
    start: string;
    end: string;
  } | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isPlayerValueInsightPopupOpen, setIsPlayerValueInsightPopupOpen] =
    useState(false);
  const [isEfficiencyInsightPopupOpen, setIsEfficiencyInsightPopupOpen] =
    useState(false);
  const [isAcquisitionInsightPopupOpen, setIsAcquisitionInsightPopupOpen] =
    useState(false);
  const [isRevenueInsightPopupOpen, setIsRevenueInsightPopupOpen] =
    useState(false);

  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const [searchParams] = useSearchParams();
  const websiteSharedUids = searchParams.get("workspace");
  const { targetVsActualPR, kpiFetchedKey } = useSelector(
    (state: any) => state.businessLeaderDashBoard,
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (!oauthTokenHeader || !websiteSharedUids || !dateRange) return;

    // Skip fetch if data was already loaded for this workspace + dateRange
    const currentFetchKey = `${websiteSharedUids}_${dateRange.start}_${dateRange.end}`;
    if (kpiFetchedKey === currentFetchKey) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const fetchData = async () => {
      try {
        const dimensionResponse =
          await buiDashboardEndPointLogicMobile.getDimensionInBizLeaderDashboardMobile(
            oauthTokenHeader,
          );
        if (!dimensionResponse || dimensionResponse.status !== 200) {
          return;
        }
        const dimensions = dimensionResponse?.data?.data || [];
        const matchedDimensions = dimensions
          ?.filter((dim: any) =>
            dim.websites?.some(
              (site: any) => site.shared_uid === websiteSharedUids,
            ),
          )
          ?.map((dim: any) => dim.dimension);

        // If no matched dimension, fall back to the first dimension in the list
        const dimensionsToUse =
          matchedDimensions?.length > 0
            ? matchedDimensions
            : dimensions?.length > 0
              ? [dimensions[0].dimension]
              : [];

        await buiDashboardEndPointLogicMobile.getTargetVsActualPR({
          oauthTokenHeader,
          queryId: targetVsActualQueryId,
          startDate: dateRange.start,
          endDate: dateRange.end,
          websiteSharedUids: websiteSharedUids?.split(",") || [],
          type: "site",
          dimension: dimensionsToUse || [],
        });
        dispatch(setKpiFetchedKey(currentFetchKey));
      } catch (error) {
        console.error("Failed to fetch target vs actual PR data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line
  }, [oauthTokenHeader, websiteSharedUids, dateRange]);

  const handleDateChange = (startDate: string, endDate: string) => {
    setDateRange({ start: startDate, end: endDate });
  };

  const formatComment = (comment: string) => {
    return comment
      .split(/(\$|\d+(?:\.\d{1,2})?)/)
      .map((part: string, index: number) =>
        /(\$|\d+(?:\.\d{1,2})?)/.test(part) ? (
          <span
            key={index}
            style={{
              color: colors.GrayLightMode[800].$value,
              fontWeight: "700",
            }}
          >
            {part !== "$" && !isNaN(Number(part))
              ? Number(part).toLocaleString()
              : part}
          </span>
        ) : (
          part
        ),
      );
  };

  const pvData: any = getValueForDashBoard("pv", targetVsActualPR);
  const pvComment = pvData?.Comment ? String(pvData.Comment) : "";
  const formattedPvComment = formatComment(pvComment);

  const cpcData: any = getValueForDashBoard("cpc", targetVsActualPR);
  const cpcComment = cpcData?.Comment ? String(cpcData.Comment) : "";
  const formattedCpcComment = formatComment(cpcComment);

  const cacData: any = getValueForDashBoard("cac", targetVsActualPR);
  const cacComment = cacData?.Comment ? String(cacData.Comment) : "";
  const formattedCacComment = formatComment(cacComment);

  const clicksData: any = getValueForDashBoard("clicks", targetVsActualPR);
  const clicksComment = clicksData?.Comment ? String(clicksData.Comment) : "";
  const formattedClicksComment = formatComment(clicksComment);

  const spendData: any = getValueForDashBoard("spend", targetVsActualPR);
  const spendComment = spendData?.Comment ? String(spendData.Comment) : "";
  const formattedSpendComment = formatComment(spendComment);

  const cvrData: any = getValueForDashBoard("cvr", targetVsActualPR);
  const cvrComment = cvrData?.Comment ? String(cvrData.Comment) : "";
  const formattedCvrComment = formatComment(cvrComment);

  const rsData: any = getValueForDashBoard("rs_Income", targetVsActualPR);
  const rsComment = rsData?.Comment ? String(rsData.Comment) : "";
  const formattedRsComment = formatComment(rsComment);

  const cpaData: any = getValueForDashBoard("cpa_Income", targetVsActualPR);
  const cpaComment = cpaData?.Comment ? String(cpaData.Comment) : "";
  const formattedCpaComment = formatComment(cpaComment);

  const totalIncomeData: any = getValueForDashBoard(
    "TOTAL_Income",
    targetVsActualPR,
  );
  const totalIncomeComment = totalIncomeData?.Comment
    ? String(totalIncomeData.Comment)
    : "";
  const formattedTotalIncomeComment = formatComment(totalIncomeComment);

  const cpaPvData: any = getValueForDashBoard("CPA_PV", targetVsActualPR);
  const cpaPvComment = cpaPvData?.Comment ? String(cpaPvData.Comment) : "";
  const formattedCpaPvComment = formatComment(cpaPvComment);

  const rsPvData: any = getValueForDashBoard("RS_PV", targetVsActualPR);
  const rsPvComment = rsPvData?.Comment ? String(rsPvData.Comment) : "";
  const formattedRsPvComment = formatComment(rsPvComment);

  return (
    <Box sx={{ px: 2 }}>
      <KpiRRfilter onDateChange={handleDateChange} />
      {isLoading ? (
        <KpiSkeleton />
      ) : (
        <Stack>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: typography.Font_weight.Medium.$value,
                color: colors.GrayLightMode[900].$value,
                mb: 1.5,
              }}
            >
              High Level KPI’s
            </Typography>
            <HighLevelKpis data={targetVsActualPR} />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: typography.Font_weight.Medium.$value,
                color: colors.GrayLightMode[900].$value,
                mb: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              Acquisition
              <EmojiObjectsOutlinedIcon
                onClick={() => setIsAcquisitionInsightPopupOpen(true)}
                sx={{
                  fontSize: typography.Size.Text.lg.$value,
                  color: colors.GrayLightMode[700].$value,
                  cursor: "pointer",
                }}
              />
            </Typography>
            <Acquisition data={targetVsActualPR} />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: typography.Font_weight.Medium.$value,
                color: colors.GrayLightMode[900].$value,
                mb: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              Revenue Streams
              <EmojiObjectsOutlinedIcon
                onClick={() => setIsRevenueInsightPopupOpen(true)}
                sx={{
                  fontSize: typography.Size.Text.lg.$value,
                  color: colors.GrayLightMode[700].$value,
                  cursor: "pointer",
                }}
              />
            </Typography>
            <RevenueStreams data={targetVsActualPR} />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: typography.Font_weight.Medium.$value,
                color: colors.GrayLightMode[900].$value,
                mb: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              Efficiency Metrics
              <EmojiObjectsOutlinedIcon
                onClick={() => setIsEfficiencyInsightPopupOpen(true)}
                sx={{
                  fontSize: typography.Size.Text.lg.$value,
                  color: colors.GrayLightMode[700].$value,
                  cursor: "pointer",
                }}
              />
            </Typography>
            <EfficiencyMetrics data={targetVsActualPR} />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: typography.Font_weight.Medium.$value,
                color: colors.GrayLightMode[900].$value,
                mb: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              Player Value
              <EmojiObjectsOutlinedIcon
                onClick={() => setIsPlayerValueInsightPopupOpen(true)}
                sx={{
                  fontSize: typography.Size.Text.lg.$value,
                  color: colors.GrayLightMode[700].$value,
                  cursor: "pointer",
                }}
              />
            </Typography>
            <PlayerValue data={targetVsActualPR} />
          </Box>
        </Stack>
      )}

      <InsightPopup
        open={isPlayerValueInsightPopupOpen}
        onClose={() => setIsPlayerValueInsightPopupOpen(false)}
        title="Player Value Insights"
        insights={[
          {
            label: "PV",
            icon: (
              <EmojiObjectsOutlinedIcon
                sx={{
                  fontSize: typography.Size.Text.lg.$value,
                  color: colors.GrayLightMode[600].$value,
                }}
              />
            ),
            description: (
              <>{formattedPvComment || "No insight data available."}</>
            ),
          },
          {
            label: "CPA PV",
            icon: (
              <EmojiObjectsOutlinedIcon
                sx={{
                  fontSize: typography.Size.Text.lg.$value,
                  color: colors.GrayLightMode[600].$value,
                }}
              />
            ),
            description: (
              <>{formattedCpaPvComment || "No insight data available."}</>
            ),
          },
          {
            label: "RS PV",
            icon: (
              <EmojiObjectsOutlinedIcon
                sx={{
                  fontSize: typography.Size.Text.lg.$value,
                  color: colors.GrayLightMode[600].$value,
                }}
              />
            ),
            description: (
              <>{formattedRsPvComment || "No insight data available."}</>
            ),
          },
        ]}
      />
      <InsightPopup
        open={isEfficiencyInsightPopupOpen}
        onClose={() => setIsEfficiencyInsightPopupOpen(false)}
        title="Efficiency Insights"
        insights={[
          {
            label: "CPC",
            icon: (
              <EmojiObjectsOutlinedIcon
                sx={{
                  fontSize: typography.Size.Text.lg.$value,
                  color: colors.GrayLightMode[600].$value,
                }}
              />
            ),
            description: (
              <>{formattedCpcComment || "No insight data available."}</>
            ),
          },
          {
            label: "CAC",
            icon: (
              <EmojiObjectsOutlinedIcon
                sx={{
                  fontSize: typography.Size.Text.lg.$value,
                  color: colors.GrayLightMode[600].$value,
                }}
              />
            ),
            description: (
              <>{formattedCacComment || "No insight data available."}</>
            ),
          },
        ]}
      />
      <InsightPopup
        open={isAcquisitionInsightPopupOpen}
        onClose={() => setIsAcquisitionInsightPopupOpen(false)}
        title="Acquisition Insights"
        insights={[
          {
            label: "Spend",
            icon: (
              <EmojiObjectsOutlinedIcon
                sx={{
                  fontSize: typography.Size.Text.lg.$value,
                  color: colors.GrayLightMode[600].$value,
                }}
              />
            ),
            description: (
              <>{formattedSpendComment || "No insight data available."}</>
            ),
          },
          {
            label: "Clicks",
            icon: (
              <EmojiObjectsOutlinedIcon
                sx={{
                  fontSize: typography.Size.Text.lg.$value,
                  color: colors.GrayLightMode[600].$value,
                }}
              />
            ),
            description: (
              <>{formattedClicksComment || "No insight data available."}</>
            ),
          },
          {
            label: "Conversion",
            icon: (
              <EmojiObjectsOutlinedIcon
                sx={{
                  fontSize: typography.Size.Text.lg.$value,
                  color: colors.GrayLightMode[600].$value,
                }}
              />
            ),
            description: (
              <>{formattedCvrComment || "No insight data available."}</>
            ),
          },
        ]}
      />
      <InsightPopup
        open={isRevenueInsightPopupOpen}
        onClose={() => setIsRevenueInsightPopupOpen(false)}
        title="Revenue Streams Insights"
        insights={[
          {
            label: "Total Income",
            icon: (
              <EmojiObjectsOutlinedIcon
                sx={{
                  fontSize: typography.Size.Text.lg.$value,
                  color: colors.GrayLightMode[600].$value,
                }}
              />
            ),
            description: (
              <>{formattedTotalIncomeComment || "No insight data available."}</>
            ),
          },
          {
            label: "CPA INCOME",
            icon: (
              <EmojiObjectsOutlinedIcon
                sx={{
                  fontSize: typography.Size.Text.lg.$value,
                  color: colors.GrayLightMode[600].$value,
                }}
              />
            ),
            description: (
              <>{formattedCpaComment || "No insight data available."}</>
            ),
          },
          {
            label: "RS INCOME",
            icon: (
              <EmojiObjectsOutlinedIcon
                sx={{
                  fontSize: typography.Size.Text.lg.$value,
                  color: colors.GrayLightMode[600].$value,
                }}
              />
            ),
            description: (
              <>{formattedRsComment || "No insight data available."}</>
            ),
          },
        ]}
      />
    </Box>
  );
};

export default KpiandBr;
